import { randomUUID } from "node:crypto";

/**
 * Represents a reusable source of values that supports both
 * on-demand generation via `next()` and iterable consumption.
 *
 * @template T - The type of values produced by the source.
 */
export interface Source<T> extends Iterable<T> {
  /**
   * Returns the next value from the source.
   *
   * This method should return a new value each time it is called.
   */
  next(): T;
}

/**
 * A source of infinite UUIDv4 strings.
 *
 * Implements a `next()` method for generating a single UUID
 * and an infinite iterable that yields UUIDs on demand.
 */
export class UuidSource implements Source<string> {
  /**
   * Returns a generated UUID string
   */
  next(): string {
    return randomUUID();
  }

  /**
   * Returns an infinite iterator of UUID strings
   *
   * @returns {Iterator<string>}
   */
  *[Symbol.iterator](): Iterator<string> {
    while (true) {
      yield this.next();
    }
  }
}
